import { Anchor, Box, Button as MantineButton, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import type { DeepPartial } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-scroll';

import { DevicePreviewModal } from '../../components/global/DevicePreviewModal/DevicePreviewModal.tsx';
import { EventFormLayout } from '../../components/layout/EventFormLayout.tsx';
import { Button } from '../../components/ui/Button/Button.tsx';
import { useIncompleteEventsItemQuery } from '../../data/incompleteEvents/useIncompleteEventsItemQuery.ts';
import { useIncompleteEventsItemUpdateMutation } from '../../data/incompleteEvents/useIncompleteEventsItemUpdateMutation.ts';
import { env } from '../../env.ts';
import { IncompleteEventForm } from '../../forms/event/IncompleteEventForm/IncompleteEventForm.tsx';
import type { IncompleteEventFormComplete } from '../../forms/event/IncompleteEventForm/schema.ts';
import { usePublishEventsMutation } from '../../helpers/publishEvents.ts';
import { useI18n } from '../../hooks/useI18n.ts';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges.ts';
import { EventPreview } from './EventPreview.tsx';
import { incompleteEventApiToIncompleteEventEditForm } from './lib/incompleteEventApiToIncompleteEventEditForm.ts';
import { incompleteEventEditFormToIncompleteEventApi } from './lib/incompleteEventEditFormToIncompleteEventApi.ts';
import { PublishEventsModal } from './PublishEventsModal.tsx';
import { UnsavedChangesModal } from './UnsavedChangesModal.tsx';

export const IncompleteEventEdit: FC<{ isCreate?: boolean }> = ({ isCreate }) => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const hiddenSubmitButtonRef = useRef<HTMLButtonElement>(null);
  const [devicePreviewOpen, setDevicePreviewOpen] = useState(false);

  const menuItems = useMemo(
    () => [
      {
        label: t('event-form_section_title--about-event') ?? 'About event',
        to: 'about-event',
      },
      {
        label: t('event-form_section_title--when-and-where') ?? 'When and where?',
        to: 'where-and-when',
      },
      {
        label: t('event-form_section_title--artwork') ?? 'Artwork',
        to: 'cover-image',
      },
      {
        label: t('event-form_section_title--links') ?? 'Links',
        to: 'links',
      },
    ],
    [t],
  );

  const { id } = useParams<{ id: string }>();

  const { data: incompleteEventData } = useIncompleteEventsItemQuery(id);
  const { mutate: mutateIncompleteEvent, isPending: mutateIncompleteEventIsPending } =
    useIncompleteEventsItemUpdateMutation(id);

  const [publishModalData, setPublishModalData] = useState<IncompleteEventFormComplete | null>(null);

  const { mutate: publishEvent, isPending: publishEventIsPending } = usePublishEventsMutation();

  const [currentFormValues, setCurrentFormValues] = useState<DeepPartial<IncompleteEventFormComplete>>(
    incompleteEventApiToIncompleteEventEditForm(incompleteEventData),
  );

  const [defaultFormValues, setDefaultFormValues] = useState<DeepPartial<IncompleteEventFormComplete> | null>(null);

  useEffect(() => {
    if (!incompleteEventData) {
      setDefaultFormValues(null);
      return;
    }

    const formData = incompleteEventApiToIncompleteEventEditForm(incompleteEventData);

    setCurrentFormValues(formData);
    setDefaultFormValues(formData);
  }, [incompleteEventData]);

  const handlePublish = () => {
    hiddenSubmitButtonRef?.current?.click();
  };

  const publishTheEvents = () => {
    if (!id || !publishModalData) {
      return;
    }

    // Save the form data to the DB first, then publish via server endpoint
    const incompleteEventApiValues = incompleteEventEditFormToIncompleteEventApi(publishModalData);
    mutateIncompleteEvent(incompleteEventApiValues, {
      onSuccess: () => {
        publishEvent(
          { incompleteEventId: id },
          {
            onSuccess: () => {
              notifications.show({
                title: t('notifications_title--publish-success') ?? 'Event Published Successfully',
                color: 'green',
                message:
                  t('notifications_message--publish-success') ??
                  'The event(s) have been successfully submitted for review.',
              });

              void navigate('/dashboard/pending');
            },
            onError: () => {
              notifications.show({
                title: t('notifications_title--publish-error') ?? 'Failed to Publish Event',
                message:
                  t('notifications_message--publish-error') ?? 'An error occurred while publishing the event(s).',
                color: 'red',
              });
            },
          },
        );
      },
      onError: (err) => {
        console.error(err);
        notifications.show({
          title: t('notifications_title--publish-error') ?? 'Failed to Publish Event',
          message: t('notifications_message--publish-error') ?? 'An error occurred while saving the event data.',
          color: 'red',
        });
      },
    });
  };

  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  const handleSaveDraft = useCallback(
    (callbacks?: { onSuccess?: () => void }) => {
      const incompleteEventApiValues = incompleteEventEditFormToIncompleteEventApi(currentFormValues);

      mutateIncompleteEvent(incompleteEventApiValues, {
        onSuccess: () => {
          const message =
            currentFormValues.name && currentFormValues.name !== ''
              ? t('notifications_message--draft-saved-with-name', {
                  name: currentFormValues.name,
                })
              : (t('notifications_message--draft-saved') ?? 'Your event draft has been saved successfully.');

          notifications.show({
            title: t('notifications_title--draft-saved') ?? 'Draft Saved Successfully',
            color: 'green',
            message,
          });

          callbacks?.onSuccess?.();
          void navigate('/dashboard/incomplete');
        },
        onError: (err) => {
          console.error(err);

          notifications.show({
            title: t('notifications_title--draft-error') ?? 'Error Saving Draft',
            message: t('notifications_message--draft-error') ?? 'Unable to save your event draft.',
            color: 'red',
          });
        },
      });
    },
    [currentFormValues, mutateIncompleteEvent, navigate, t],
  );

  const preview = useMemo(() => {
    if (!currentFormValues) {
      return null;
    }

    return (
      <Stack align="flex-end" gap="xs" pos="sticky" top={24}>
        <Stack align="flex-end" gap="xxxs" h={195 - 24} justify="flex-end" w={320}>
          <Button
            intent="primary"
            loading={publishEventIsPending}
            text={t('button--publish') ?? 'Publish'}
            type="button"
            onClick={handlePublish}
          />
        </Stack>
        <EventPreview
          eventPreviewData={{
            ...currentFormValues,
            afterImageContent: env.VITE_FRONTEND_URL ? (
              <Box mb={4} mt={8}>
                <MantineButton color="dark" size="xs" variant="outline" onClick={() => setDevicePreviewOpen(true)}>
                  {t('event-form_button--show-on-device') ?? 'Show on device'}
                </MantineButton>
              </Box>
            ) : undefined,
          }}
        />
        <Button
          intent="secondary"
          loading={mutateIncompleteEventIsPending}
          text={t('button--save-draft') ?? 'Save draft'}
          type="button"
          onClick={() => handleSaveDraft()}
        />
      </Stack>
    );
  }, [currentFormValues, handleSaveDraft, mutateIncompleteEventIsPending, publishEventIsPending, t]);

  const incompleteEventForm = useMemo(() => {
    if (!defaultFormValues) {
      return null;
    }

    return (
      <IncompleteEventForm
        defaultValues={defaultFormValues}
        submitButton={<button hidden ref={hiddenSubmitButtonRef} type="submit" />}
        onCurrentFormValueChange={setCurrentFormValues}
        onSubmit={setPublishModalData}
      />
    );
  }, [defaultFormValues]);

  const hasUnsavedChanges = useUnsavedChanges(defaultFormValues, currentFormValues);

  const handleBackToDashboard = () => {
    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    void navigate('/dashboard/incomplete');
  };

  const handleUnsavedChangesClose = () => setIsUnsavedChangesModalOpen(false);

  const handleSaveChangesAndNavigate = () => {
    handleSaveDraft({ onSuccess: () => void navigate('/dashboard/incomplete') });
  };

  const handleDiscardChangesAndNavigate = () => void navigate('/dashboard/incomplete');

  return (
    <>
      <UnsavedChangesModal
        opened={isUnsavedChangesModalOpen}
        saveChangesIsPending={mutateIncompleteEventIsPending}
        onClose={handleUnsavedChangesClose}
        onDontSaveChanges={handleDiscardChangesAndNavigate}
        onSaveChanges={handleSaveChangesAndNavigate}
      />
      <PublishEventsModal
        formEventFull={publishModalData}
        isOpen={!!publishModalData}
        isPublishing={publishEventIsPending}
        onClose={() => setPublishModalData(null)}
        onConfirmPublish={publishTheEvents}
      />
      <DevicePreviewModal
        formData={currentFormValues}
        opened={devicePreviewOpen}
        onClose={() => setDevicePreviewOpen(false)}
      />

      <EventFormLayout
        backButton={{
          text: t('button--back-to-dashboard') ?? 'Go back to Dashboard',
          onClick: handleBackToDashboard,
        }}
        centralContent={incompleteEventForm}
        rightContent={preview}
        sectionSubtitle={
          isCreate ? (t('title--create-event') ?? 'Create event') : (t('title--edit-event') ?? 'Edit incomplete event')
        }
        sectionTitle={currentFormValues.name && currentFormValues.name !== '' ? currentFormValues.name : 'Event'}
        sideMenuContent={
          <Stack align="flex-start" gap="xxs" pos="sticky" top={110 + 40}>
            {menuItems.map((menuItem, i) => (
              <Anchor
                hashSpy
                smooth
                spy
                activeStyle={{ color: 'purple', textDecoration: 'underline' }}
                c="gray.8"
                component={Link}
                fw="600"
                key={menuItem.to}
                offset={-100 - i * 33}
                size="sm"
                to={menuItem.to}
                underline="hover"
              >
                {menuItem.label}
              </Anchor>
            ))}
          </Stack>
        }
      />
    </>
  );
};
